import type { MessageInputRef } from "@agent-ui/features/chat/components/chat-elements/message-input/MessageInput";
import type { Editor } from "@tiptap/react";
import type { Industry } from "@v2/features/home/components/prompt-suggestions/industries";
import type {
  PromptCategory,
  PromptCategoryConfig,
} from "@v2/features/home/components/prompt-suggestions/types";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import AssetComponent from "@agent-ui/features/chat/components/chat-elements/message-input/assetNodeExtension";
import IntegrationObjectComponent from "@agent-ui/features/chat/components/chat-elements/message-input/integrationObjectNodeExtension";
import { MessageInput } from "@agent-ui/features/chat/components/chat-elements/message-input/MessageInput";
import ProductComponent from "@agent-ui/features/chat/components/chat-elements/message-input/productNodeExtension";
import HardBreakMarkdown from "@agent-ui/features/chat/extensions/hardBreakMarkdown";
import useChatMessageLimits from "@agent-ui/features/chat/hooks/useChatMessageLimits";
import { useQuickPrompt } from "@agent-ui/features/chat/hooks/useQuickPrompt";
import { useCurrentChatContext } from "@agent-ui/features/chat/providers/CurrentChatContext";
import {
  chatEditorHandleKeyDown,
  chatEditorHandlePaste,
  getCleanedChatPrompt,
  useChatEditorOnUpdate,
} from "@agent-ui/features/chat/utils/tiptapUtils";
import { Button } from "@replo/design-system/components/button/Button";
import { Tag } from "@replo/design-system/components/tag/Tag";
import Placeholder from "@tiptap/extension-placeholder";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TutorialAnchorWrapper } from "@v2/features/activation/components/HomepageTutorial";
import { PROMPT_CATEGORIES } from "@v2/features/home/components/prompt-suggestions/categories";
import { PROMPTS_BY_CATEGORY } from "@v2/features/home/components/prompt-suggestions/categoryPrompts";
import { INDUSTRY_ICONS } from "@v2/features/home/components/prompt-suggestions/industries";
import { INDUSTRY_PROMPTS } from "@v2/features/home/components/prompt-suggestions/industryPrompts";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import { INTEGRATION_CONFIGS } from "@v2/features/settings/shop/integrations/config";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { routes } from "@v2/utils/router";
import { X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { generatePath, useLocation, useNavigate } from "react-router-dom";
import { Markdown as TiptapMarkdown } from "tiptap-markdown";

import { ChipButton } from "./ChipButton";
import { IndustrySelector } from "./IndustrySelector";
import { PromptCarousel } from "./PromptCarousel";
import { useCyclingPlaceholder } from "./useCyclingPlaceholder";

const INTEGRATION_BANNER_LOGOS = [
  INTEGRATION_CONFIGS.Klaviyo,
  INTEGRATION_CONFIGS.GA4,
  INTEGRATION_CONFIGS.Meta,
  INTEGRATION_CONFIGS.Hotjar,
  INTEGRATION_CONFIGS.GoogleTagManager,
  INTEGRATION_CONFIGS.TikTok,
  INTEGRATION_CONFIGS.Pinterest,
] as const;

const heroTextOptions = [
  "Build your next winning idea",
  "Create pages customers say yes to",
  "Turn ideas into revenue",
  "Bring concepts to conversion",
  "Turn traffic into customers",
  "Build landing pages that perform",
];

export function StartChat() {
  const { files, setFiles, addFiles } = useCurrentChatContext();
  const [selectedCategory, setSelectedCategory] =
    useState<PromptCategory | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState<Industry | null>(
    null,
  );
  const [heroText] = useState(
    () => heroTextOptions[Math.floor(Math.random() * heroTextOptions.length)]!,
  );

  const homeEditorRef = useRef<Editor | null>(null);
  const messageInputRef = useRef<MessageInputRef>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const projectId = useCurrentProjectId();
  const logEvent = useLogAnalytics();
  const shouldShowNewIntegrationsUi = isFeatureEnabled("integrations-v2-ui");
  const shouldShowConnectToolsBanner =
    !location.pathname.startsWith("/shops/") || shouldShowNewIntegrationsUi;

  const { hasReachedLimit } = useChatMessageLimits();
  const { chatPlaceholderStyle } = useCyclingPlaceholder();

  const currentPrompts = selectedCategory
    ? PROMPTS_BY_CATEGORY[selectedCategory]
    : [];

  const selectedCategoryConfig = useMemo((): PromptCategoryConfig | null => {
    if (!selectedCategory) {
      return null;
    }
    return PROMPT_CATEGORIES.find((cat) => cat.id === selectedCategory) ?? null;
  }, [selectedCategory]);

  const handleClearCategory = useCallback(() => {
    setSelectedCategory(null);
    setSelectedIndustry(null);
  }, []);

  const handleCategoryChange = useCallback((category: PromptCategory) => {
    setSelectedCategory(category);
  }, []);

  const handleBackFromIndustry = useCallback(() => {
    setSelectedCategory(null);
    setSelectedIndustry(null);
  }, []);

  const handleIndustrySelect = useCallback((industry: Industry) => {
    setSelectedIndustry(industry);
  }, []);

  const handleBackFromIndustryPrompts = useCallback(() => {
    setSelectedIndustry(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    const editor = homeEditorRef.current;
    if (!editor || !projectId) {
      return;
    }

    if (hasReachedLimit) {
      return;
    }

    const prompt = getCleanedChatPrompt(editor);
    if (!prompt) {
      return;
    }

    editor.commands.setContent("", false);
    setFiles([]);

    // NOTE (Bobby, 2025-07-30): Storing this through react router's state so in the browser's session history. Potentially want to make this
    // in a shared context but the home and chat were just in pretty unrelated contexts so doing this for now.
    navigate(generatePath(routes.agent.chat, { projectId }), {
      state: {
        initialMessage: prompt,
        initialFiles: files,
      },
    });
  }, [projectId, navigate, files, hasReachedLimit, setFiles]);

  const chatEditorOnUpdate = useChatEditorOnUpdate();

  // Set up the Tiptap editor
  const homeEditor = useEditor({
    extensions: [
      StarterKit.configure({
        hardBreak: false,
        paragraph: {
          HTMLAttributes: {
            style: "white-space: pre-wrap",
          },
        },
      }),
      AssetComponent,
      ProductComponent,
      IntegrationObjectComponent,
      HardBreakMarkdown,
      TiptapMarkdown.configure({
        /*
         * NOTE (Kevin, 2025-09-24):
         * breaks=false because the HardBreak extension emits <br /> directly.
         * html=true keeps <chat-token> placeholders intact through markdown.
         * tightLists=true prevents blank paragraphs between list items.
         */
        breaks: false,
        html: true,
        tightLists: true,
      }),
      Placeholder.configure({
        placeholder: "Ask Replo anything...",
      }),
    ],
    content: "",
    onUpdate: chatEditorOnUpdate,
    autofocus: true,
    editorProps: {
      attributes: {
        style: "white-space: pre-wrap;",
        class: "home-chat-input text-sm",
      },
      handleKeyDown: chatEditorHandleKeyDown(() => {
        void handleSubmit();
      }),
      handlePaste: chatEditorHandlePaste(addFiles),
    },
  });

  useEffect(() => {
    homeEditorRef.current = homeEditor;
  }, [homeEditor]);

  const handleQuickPrompt = useQuickPrompt({
    editor: homeEditor,
    onSelectUploadImage: () => {
      void messageInputRef.current?.triggerFileUpload();
    },
    onSelectProduct: () => {
      void messageInputRef.current?.triggerContextMenu();
    },
  });

  const handleSelectPrompt = useCallback(
    (prompt: string, action?: "upload") => {
      logEvent("chat.selectRecommendationPrompt", {
        origin: "home",
        prompt,
        category:
          selectedCategory === "by-industry"
            ? `by-industry:${selectedIndustry}`
            : selectedCategory ?? undefined,
      });
      handleQuickPrompt(prompt, action);
    },
    [logEvent, selectedCategory, selectedIndustry, handleQuickPrompt],
  );

  const selectedCategoryChip = (
    <AnimatePresence>
      {selectedCategoryConfig && (
        <div className="flex items-center gap-1.5">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
            className="flex items-center gap-1.5 px-3 py-2 h-8 border border-component-border rounded-md text-sm font-normal text-default bg-sheet"
          >
            {React.cloneElement(selectedCategoryConfig.icon, {
              className: "w-3.5 h-3.5",
            })}
            <span>{selectedCategoryConfig.label}</span>
            <Button
              icon
              variant="tertiary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleClearCategory();
              }}
              aria-label="Clear category"
              UNSAFE_className="ml-0.5 size-4 p-0"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </motion.div>

          {selectedIndustry && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.15 }}
              className="flex items-center gap-1.5 px-3 py-2 h-8 border border-component-border rounded-md text-sm font-normal text-default bg-sheet"
            >
              {React.cloneElement(INDUSTRY_ICONS[selectedIndustry], {
                className: "w-3.5 h-3.5",
              })}
              <span>{selectedIndustry}</span>
              <Button
                icon
                variant="tertiary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBackFromIndustryPrompts();
                }}
                aria-label="Clear industry"
                UNSAFE_className="ml-0.5 size-4 p-0"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </motion.div>
          )}
        </div>
      )}
    </AnimatePresence>
  );

  return (
    // Inspiration for chat glow animation here: https://ui.aceternity.com/components/background-gradient
    <div className="flex flex-col w-full items-center justify-center min-w-0 gap-8 md:gap-12">
      <style>
        {`
          .home-chat-input.tiptap p.is-editor-empty:first-child::before {
            color: var(--color-neutral-500);
            content: var(--chat-placeholder, "Ask Replo anything...") " ";
            float: left;
            height: 0;
            pointer-events: none;
            white-space: pre;
            margin-right: 0.35ch;
          }

          .home-chat-input.tiptap * {
            max-width: 100%;
            white-space: pre-wrap;
            word-break: break-word;
            overflow-wrap: anywhere;
          }
          .home-chat-input.tiptap p {
            white-space: pre-wrap;
            word-break: break-word;
            overflow-wrap: anywhere;
          }

          .no-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .no-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }

          .home-chat-shell {
            position: relative;
            isolation: isolate;
            -webkit-tap-highlight-color: transparent;
          }

          /* Soft animated gradient glow behind the input (blurred layer) */
          .home-chat-shell::before {
            content: '';
            position: absolute;
            inset: -12px;
            border-radius: 24px;
            pointer-events: none;
            z-index: 0;
            opacity: 0.35;
            filter: blur(20px);
            background: radial-gradient(circle farthest-side at 0% 100%, rgba(255,160,40,0.10), transparent 75%),
                        radial-gradient(circle farthest-side at 100% 0%, rgba(79,70,245,0.10), transparent 78%),
                        radial-gradient(circle farthest-side at 100% 100%, rgba(255,70,60,0.10), transparent 80%),
                        radial-gradient(circle farthest-side at 0% 0%, rgba(79,70,245,0.10), transparent 78%),
                        radial-gradient(circle farthest-side at 50% 50%, rgba(255,130,85,0.10), transparent 85%);
            background-blend-mode: screen;
            background-size: 200% 200%;
            animation: aceternity-gradient 6s ease infinite;
            transition: opacity 400ms ease;
          }

          @media (hover: hover) {
            .home-chat-shell:hover::before {
              opacity: 0.5;
            }
          }

          .home-chat-shell:focus-within::before {
            opacity: 0.5;
          }

          /* Gradient border */
          .home-chat-border {
            position: absolute;
            inset: -4px;
            border-radius: 20px;
            z-index: 0;
            background: radial-gradient(circle farthest-side at 0% 100%, rgba(255,160,40,0.10), transparent 75%),
                        radial-gradient(circle farthest-side at 100% 0%, rgba(79,70,245,0.10), transparent 78%),
                        radial-gradient(circle farthest-side at 100% 100%, rgba(255,70,60,0.10), transparent 80%),
                        radial-gradient(circle farthest-side at 0% 0%, rgba(79,70,245,0.10), transparent 78%),
                        radial-gradient(circle farthest-side at 50% 50%, rgba(255,130,85,0.10), transparent 85%);
            background-blend-mode: screen;
            background-size: 200% 200%;
            animation: aceternity-gradient 6s ease infinite;
            opacity: 0.35;
          }

          @media (hover: hover) {
            .home-chat-shell:hover .home-chat-border {
              opacity: 0.5;
            }
          }

          .home-chat-shell:focus-within .home-chat-border {
            opacity: 0.5;
          }

          /* Thin 0.5px gradient outline around the outer glow border */
          .home-chat-shell::after {
            content: '';
            position: absolute;
            inset: -4.5px;
            border-radius: 20.5px;
            padding: 0.5px;
            pointer-events: none;
            z-index: 0;
            background: var(--color-gradient-ai-30);
            -webkit-mask:
              linear-gradient(#fff 0 0) content-box,
              linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask:
              linear-gradient(#fff 0 0) content-box,
              linear-gradient(#fff 0 0);
            mask-composite: exclude;
          }

          /* Form sits above the gradient border */
          .home-chat-shell form {
            position: relative;
            z-index: 1;
          }

          /* MessageInput styling - target the container inside Popover */
          .home-chat-shell form div.bg-white {
            border: none !important;
            background: white !important;
            border-radius: 16px !important;
            box-shadow: none !important;
          }


          @keyframes aceternity-gradient {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .home-chat-shell::before {
              animation: none;
            }
          }

          /* Disable animations and transitions on touch devices to prevent flickering */
          @media (pointer: coarse) {
            .home-chat-shell::before {
              animation: none;
              transition: none;
              background-size: 100% 100%;
            }

            .home-chat-border {
              animation: none;
            }

            .home-chat-shell:focus-within::before,
            .home-chat-shell:focus-within .home-chat-border {
              opacity: 0.5;
            }
          }
        `}
      </style>

      <div className="flex items-center gap-3">
        <div className="font-semibold text-default cursor-default text-center text-xl md:text-3xl leading-tight">
          {heroText}
        </div>
        <Tag variant="info" size="base">
          Agent
        </Tag>
      </div>

      <div className="flex flex-col w-full items-center justify-center min-w-0">
        <div className="relative flex flex-col gap-6 w-full md:px-6 max-w-full md:max-w-[850px]">
          <div className="relative">
            <TutorialAnchorWrapper
              anchorId="homepage-chat"
              side="top"
              sideOffset={24}
              align="center"
            >
              <div className="home-chat-shell">
                <div className="home-chat-border" />
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void handleSubmit();
                  }}
                  className="w-full cursor-text"
                  style={chatPlaceholderStyle}
                >
                  <MessageInput
                    ref={messageInputRef}
                    isChatLoading={false}
                    editor={homeEditor}
                    allowAttachments={true}
                    files={files}
                    setFiles={setFiles}
                    editorClassName="min-h-24 max-h-48 text-sm"
                    leftEnhancer={selectedCategoryChip}
                  />
                </form>
                {shouldShowConnectToolsBanner ? (
                  <div
                    role="button"
                    tabIndex={0}
                    className="relative z-[1] flex items-center justify-between px-4 py-2.5 cursor-pointer transition-colors rounded-b-2xl"
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(
                        generatePath(routes.shops.integrations.list, {
                          projectId: projectId ?? "",
                        }),
                      );
                    }}
                    onKeyDown={(keyboardEvent) => {
                      if (
                        keyboardEvent.key === "Enter" ||
                        keyboardEvent.key === " "
                      ) {
                        keyboardEvent.preventDefault();
                        navigate(
                          generatePath(routes.shops.integrations.list, {
                            projectId: projectId ?? "",
                          }),
                        );
                      }
                    }}
                  >
                    <span
                      className="text-sm"
                      style={{
                        backgroundImage: "var(--color-gradient-ai-100)",
                        backgroundSize: "200% 100%",
                        backgroundPosition: "80% 0%",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      Connect your tools to Replo
                    </span>
                    <div className="flex items-center -space-x-2">
                      {INTEGRATION_BANNER_LOGOS.map((integrationConfig) => (
                        <img
                          key={integrationConfig.value}
                          src={integrationConfig.image}
                          alt={integrationConfig.title}
                          className="size-6 rounded-full border-2 border-white object-contain bg-white"
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </TutorialAnchorWrapper>
          </div>

          {/* No category selected: show category chips */}
          {selectedCategory === null && (
            <div className="relative z-[1] flex gap-3 overflow-x-auto no-scrollbar justify-center">
              {PROMPT_CATEGORIES.map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.03 }}
                >
                  <ChipButton
                    onClick={() => handleCategoryChange(category.id)}
                    startEnhancer={React.cloneElement(category.icon, {
                      className: "w-3.5 h-3.5",
                    })}
                  >
                    {category.label}
                  </ChipButton>
                </motion.div>
              ))}
            </div>
          )}

          {/* Industry category selected but no industry yet: show industry chips with search */}
          {selectedCategory === "by-industry" && selectedIndustry === null && (
            <IndustrySelector
              onBack={handleBackFromIndustry}
              onSelectIndustry={handleIndustrySelect}
            />
          )}

          {/* Industry AND specific industry selected: show industry-specific prompts */}
          {selectedCategory === "by-industry" && selectedIndustry !== null && (
            <PromptCarousel
              prompts={INDUSTRY_PROMPTS[selectedIndustry]}
              onSelectPrompt={handleSelectPrompt}
            />
          )}

          {/* Other category selected: show prompt cards */}
          {selectedCategory !== null && selectedCategory !== "by-industry" && (
            <PromptCarousel
              prompts={currentPrompts}
              onSelectPrompt={handleSelectPrompt}
            />
          )}
        </div>
      </div>
    </div>
  );
}
